import { openDB, type IDBPDatabase } from 'idb';
import { fetchWithAuth, AuthRequiredError } from './auth';
import { connectivity } from './connectivity';
import { loadBlob } from './blobStore';

const DB_NAME = 'eva-field';
const DB_VERSION = 1;
const STORE = 'sync_queue';

export type QueueStatus = 'pending' | 'uploading' | 'confirmed' | 'failed';

export interface QueueItem {
  id: string;                 // local UUID — internal key
  org_id: string;
  job_id: string;
  session_id: string;
  photo_id: string;           // server-side idempotency key
  sequence_index: number;
  local_blob_key: string;     // key into a separate IDB blob store (Prompt 03)
  metadata: {
    captured_at: string;      // ISO 8601 UTC
    checksum: string;         // sha256 hex (lowercase)
    device_info?: Record<string, unknown>;
  };
  status: QueueStatus;
  attempts: number;
  last_attempt_at: string | null;
  confirmed_at: string | null;
  error_detail: string | null;
  created_at: string;
}

const BACKOFF_MS = [5_000, 15_000, 30_000, 60_000, 120_000];
const MAX_ATTEMPTS = 5;
const STALE_UPLOADING_MS = 2 * 60_000;

const QUEUE_CHANGE_EVENT = 'eva-field:sync-queue-changed';

function notifyQueueChanged(): void {
  window.dispatchEvent(new CustomEvent(QUEUE_CHANGE_EVENT));
}

let dbPromise: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) {
          const s = d.createObjectStore(STORE, { keyPath: 'id' });
          s.createIndex('by_job_seq', ['job_id', 'sequence_index']);
          s.createIndex('by_status', 'status');
          s.createIndex('by_job_status', ['job_id', 'status']);
        }
      },
    });
  }
  return dbPromise;
}

let draining = false;

/** Add a confirmed photo to the upload queue. Triggers a drain attempt. */
export async function enqueue(
  item: Omit<
    QueueItem,
    'status' | 'attempts' | 'last_attempt_at' | 'confirmed_at' | 'error_detail' | 'created_at'
  >
): Promise<void> {
  const full: QueueItem = {
    ...item,
    status: 'pending',
    attempts: 0,
    last_attempt_at: null,
    confirmed_at: null,
    error_detail: null,
    created_at: new Date().toISOString(),
  };
  const d = await db();
  await d.put(STORE, full);
  notifyQueueChanged();
  void drain();
}

/** On app open or tab focus: revert any 'uploading' items to 'pending'. */
export async function resume(): Promise<void> {
  const d = await db();
  const tx = d.transaction(STORE, 'readwrite');
  const idx = tx.store.index('by_status');
  let cursor = await idx.openCursor('uploading');
  while (cursor) {
    const item = cursor.value as QueueItem;
    item.status = 'pending';
    await cursor.update(item);
    notifyQueueChanged();
    cursor = await cursor.continue();
  }
  await tx.done;
  void drain();
}

export async function getFailedItems(jobId: string): Promise<QueueItem[]> {
  const d = await db();
  return (await d.getAllFromIndex(STORE, 'by_job_status', [jobId, 'failed'])) as QueueItem[];
}

/** Requeue failed items so a manual Retry can upload after fixing server/network issues. */
export async function retryFailedItems(jobId?: string): Promise<number> {
  const d = await db();
  const tx = d.transaction(STORE, 'readwrite');
  const failed = (await tx.store.index('by_status').getAll('failed')) as QueueItem[];
  let count = 0;
  for (const item of failed) {
    if (jobId && item.job_id !== jobId) continue;
    item.status = 'pending';
    item.attempts = 0;
    item.last_attempt_at = null;
    item.error_detail = null;
    await tx.store.put(item);
    count += 1;
  }
  await tx.done;
  if (count > 0) notifyQueueChanged();
  if (count > 0) void drain();
  return count;
}

/** All queue items for a job, in sequence order. Used by the session gallery. */
export async function getItemsByJob(jobId: string): Promise<QueueItem[]> {
  const d = await db();
  const all = (await d.getAll(STORE)) as QueueItem[];
  return all
    .filter((x) => x.job_id === jobId)
    .sort((a, b) => a.sequence_index - b.sequence_index);
}

export async function updateQueueItemMetadata(
  itemId: string,
  metadata: QueueItem['metadata'],
): Promise<void> {
  const d = await db();
  const item = (await d.get(STORE, itemId)) as QueueItem | undefined;
  if (!item) return;
  item.metadata = metadata;
  await d.put(STORE, item);
  notifyQueueChanged();
}

export async function updateQueueItemDeviceInfo(
  itemId: string,
  deviceInfo: Record<string, unknown>,
): Promise<void> {
  const d = await db();
  const item = (await d.get(STORE, itemId)) as QueueItem | undefined;
  if (!item) return;
  item.metadata = {
    ...item.metadata,
    device_info: deviceInfo,
  };
  await d.put(STORE, item);
  notifyQueueChanged();
}

export async function listQueueItems(): Promise<QueueItem[]> {
  const d = await db();
  return (await d.getAll(STORE)) as QueueItem[];
}

export interface QueueSummary {
  total: number;
  pending: number;
  uploading: number;
  confirmed: number;
  failed: number;
  affectedJobCount: number;
}

export async function getQueueSummary(): Promise<QueueSummary> {
  const items = await listQueueItems();
  const active = items.filter((item) => item.status !== 'confirmed');
  return {
    total: items.length,
    pending: items.filter((item) => item.status === 'pending').length,
    uploading: items.filter((item) => item.status === 'uploading').length,
    confirmed: items.filter((item) => item.status === 'confirmed').length,
    failed: items.filter((item) => item.status === 'failed').length,
    affectedJobCount: new Set(active.map((item) => item.job_id)).size,
  };
}

export async function recoverStaleUploading(maxAgeMs = STALE_UPLOADING_MS): Promise<number> {
  const d = await db();
  const tx = d.transaction(STORE, 'readwrite');
  const idx = tx.store.index('by_status');
  const now = Date.now();
  let recovered = 0;
  let cursor = await idx.openCursor('uploading');
  while (cursor) {
    const item = cursor.value as QueueItem;
    const last = item.last_attempt_at ? Date.parse(item.last_attempt_at) : 0;
    if (!last || now - last > maxAgeMs) {
      item.status = 'pending';
      item.error_detail = item.error_detail ?? 'Recovered from interrupted upload.';
      await cursor.update(item);
      recovered += 1;
    }
    cursor = await cursor.continue();
  }
  await tx.done;
  if (recovered > 0) notifyQueueChanged();
  if (recovered > 0) void drain();
  return recovered;
}

/** Drain queue strictly one item at a time, in (job_id, sequence_index) ASC order. */
export async function drain(): Promise<void> {
  if (draining) return;
  if (!connectivity.isOnline()) return;
  draining = true;
  try {
    while (connectivity.isOnline()) {
      const next = await peekNextPending();
      if (!next) return;
      await uploadOne(next);
    }
  } finally {
    draining = false;
  }
}

async function peekNextPending(): Promise<QueueItem | null> {
  const d = await db();
  const all = (await d.getAllFromIndex(STORE, 'by_status', 'pending')) as QueueItem[];
  if (all.length === 0) return null;
  all.sort((a, b) =>
    a.job_id === b.job_id ? a.sequence_index - b.sequence_index : a.job_id.localeCompare(b.job_id)
  );
  return all[0];
}

async function uploadOne(item: QueueItem): Promise<void> {
  const d = await db();

  item.status = 'uploading';
  item.last_attempt_at = new Date().toISOString();
  await d.put(STORE, item);
  notifyQueueChanged();

  try {
    const blob = await loadBlob(item.local_blob_key);
    const form = new FormData();
    form.append('photo', blob, `${item.sequence_index}_${item.photo_id}.jpg`);
    form.append(
      'metadata',
      JSON.stringify({
        photo_id: item.photo_id,
        job_id: item.job_id,
        session_id: item.session_id,
        sequence_index: item.sequence_index,
        captured_at: item.metadata.captured_at,
        checksum: item.metadata.checksum,
        device_info: item.metadata.device_info ?? {},
      })
    );

    const resp = await fetchWithAuth(
      '/api/field/photos/upload',
      {
        method: 'POST',
        body: form,
      },
      { orgId: item.org_id },
    );

    if (resp.ok) {
      const body = await resp.json();
      item.status = 'confirmed';
      item.confirmed_at = body.confirmed_at ?? new Date().toISOString();
      item.error_detail = null;
      await d.put(STORE, item);
      notifyQueueChanged();
      window.dispatchEvent(new CustomEvent('eva-field:sync-confirmed', { detail: item }));
      return;
    }

    if (resp.status === 409) {
      // Server is the source of truth on sequence; surface to UI for renumber/retry.
      item.status = 'failed';
      item.error_detail = `409: ${(await resp.text()).slice(0, 500)}`;
      await d.put(STORE, item);
      notifyQueueChanged();
      window.dispatchEvent(new CustomEvent('eva-field:sync-failed', { detail: item }));
      return;
    }

    if (resp.status >= 500) {
      throw new Error(`server ${resp.status}`);
    }

    // Other 4xx (422, 404, etc.) — non-retryable client error.
    item.status = 'failed';
    item.error_detail = `${resp.status}: ${(await resp.text()).slice(0, 500)}`;
    await d.put(STORE, item);
    notifyQueueChanged();
    window.dispatchEvent(new CustomEvent('eva-field:sync-failed', { detail: item }));
  } catch (e) {
    if (e instanceof AuthRequiredError) {
      item.status = 'pending';
      await d.put(STORE, item);
      notifyQueueChanged();
      window.dispatchEvent(new CustomEvent('eva-field:auth-required'));
      throw e;
    }

    item.attempts += 1;
    if (item.attempts >= MAX_ATTEMPTS) {
      item.status = 'failed';
      item.error_detail = `max attempts: ${(e as Error).message}`;
      await d.put(STORE, item);
      notifyQueueChanged();
      window.dispatchEvent(new CustomEvent('eva-field:sync-failed', { detail: item }));
      return;
    }
    item.status = 'pending';
    item.error_detail = (e as Error).message;
    await d.put(STORE, item);
    notifyQueueChanged();
    const wait = BACKOFF_MS[Math.min(item.attempts - 1, BACKOFF_MS.length - 1)];
    await sleep(wait);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

connectivity.onStatusChange((online) => {
  if (online) void drain();
});

window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void resume();
});
